import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Clock, CalendarCheck, Loader2, Users, Globe } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { ymd, fmtTime } from '../lib/format';
import { SpecialistCard, AnySpecialistCard } from '../components/SpecialistCard';
import { useAuth } from '../lib/auth';

function monthLabel(d) {
  return d.toLocaleString('en-CA', { month: 'long', year: 'numeric' });
}

function buildMonthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const startWeekday = (first.getDay() + 6) % 7; // Monday=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function BookSelectTimePage() {
  const { typeId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [type, setType] = useState(null);
  const [specialists, setSpecialists] = useState([]);
  const [advisorId, setAdvisorId] = useState('');  // '' => Any
  const [languageFilter, setLanguageFilter] = useState('');
  const [loadingSpec, setLoadingSpec] = useState(true);
  const today = useMemo(() => new Date(), []);
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [rangeMap, setRangeMap] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingRange, setLoadingRange] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    api.listTypes().then((all) => {
      const t = all.find((x) => x.id === typeId);
      if (!t) { nav('/book'); return; }
      setType(t);
    });
    // Load rich specialist cards. Use authenticated route when signed in so we
    // get preferences + "is_my_specialist" highlights.
    (async () => {
      setLoadingSpec(true);
      try {
        if (user) {
          const data = await api.myBookingSpecialists({ appointment_type_id: typeId });
          setSpecialists(data.specialists || []);
          // Default to existing assigned specialist when one exists.
          const mine = (data.specialists || []).find((s) => s.is_my_specialist);
          if (mine) setAdvisorId(mine.advisor_id);
        } else {
          const list = await api.bookingSpecialists({ appointment_type_id: typeId });
          setSpecialists(list || []);
        }
      } finally { setLoadingSpec(false); }
    })();
  }, [typeId, nav, user]);

  useEffect(() => {
    if (!type) return;
    setLoadingRange(true);
    const first = new Date(view.year, view.month, 1);
    const start = ymd(first > today ? first : today);
    const days = new Date(view.year, view.month + 1, 0).getDate() + 7;
    api.availabilityRange(type.id, start, days, advisorId || undefined)
      .then(setRangeMap)
      .finally(() => setLoadingRange(false));
  }, [type, view, advisorId, today]);

  useEffect(() => {
    if (!selectedDate || !type) return;
    setLoadingSlots(true);
    setSelectedSlot(null);
    api.availability(type.id, ymd(selectedDate), advisorId || undefined)
      .then((d) => setSlots(d.slots || []))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate, type, advisorId]);

  const matrix = buildMonthMatrix(view.year, view.month);
  const navMonth = (delta) => {
    const d = new Date(view.year, view.month + delta, 1);
    setView({ year: d.getFullYear(), month: d.getMonth() });
  };

  // Phase 3 — language filter for the specialist picker
  const availableLanguages = useMemo(() => {
    const all = new Set();
    specialists.forEach((s) => (s.languages || []).forEach((l) => l && all.add(l)));
    return Array.from(all).sort();
  }, [specialists]);

  const filteredSpecialists = useMemo(() => {
    if (!languageFilter) return specialists;
    const lf = languageFilter.toLowerCase();
    return specialists.filter((s) =>
      (s.languages || []).some((l) => (l || '').toLowerCase() === lf));
  }, [specialists, languageFilter]);

  const proceed = () => {
    if (!selectedSlot) return;
    sessionStorage.setItem('wyse_booking_draft', JSON.stringify({
      typeId: type.id,
      start: selectedSlot.start,
      advisorId: selectedSlot.advisor_id,
    }));
    nav(`/book/${type.id}/details`);
  };

  return (
    <>
      <Header />
      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <Link to="/book" className="inline-flex items-center gap-1.5 text-[13.5px] text-neutral-500 hover:text-[#c89200] mb-5">
            <ArrowLeft className="w-4 h-4" /> Back to services
          </Link>
          <p className="section-eyebrow mb-2">Step 2 of 3</p>
          <h1 className="text-[32px] md:text-[40px] font-extrabold text-[#14110f]">Pick a date & time</h1>
          {type && (
            <div className="mt-3 flex flex-wrap items-center gap-3 text-[14px] text-neutral-600">
              <span className="font-semibold text-[#14110f]">{type.name}</span>
              <span className="w-1 h-1 rounded-full bg-neutral-300" />
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {type.duration_minutes} min</span>
              <span className="w-1 h-1 rounded-full bg-neutral-300" />
              <span className="capitalize">{type.location_type.replace('_', ' ')}</span>
              {type.price > 0 && <><span className="w-1 h-1 rounded-full bg-neutral-300" /><span className="font-semibold text-[#c89200]">${type.price}</span></>}
            </div>
          )}

          <div className="mt-8 space-y-6">
            {/* Specialist selection */}
            <div className="rounded-xl border border-neutral-100 p-5 md:p-7 bg-white shadow-[0_20px_45px_-25px_rgba(20,17,15,0.18)]">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-neutral-400 font-semibold">Step 2a</p>
                  <h3 className="text-[18px] font-bold text-[#14110f] flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#c89200]" />Choose your Tax Specialist
                  </h3>
                </div>
                <p className="text-[12.5px] text-neutral-500 max-w-md">
                  Select a specific specialist or let us auto-match the best fit based on availability, language and workload.
                </p>
              </div>
              {loadingSpec ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {[...Array(3)].map((_, i) => <div key={i} className="h-[150px] rounded-xl bg-neutral-100 animate-pulse" />)}
                </div>
              ) : (
                <>
                  {availableLanguages.length > 1 && (
                    <div data-testid="booking-language-filter"
                         className="mb-4 flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] uppercase tracking-wider text-neutral-500 font-semibold flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5" />Filter by language
                      </span>
                      <button data-testid="lang-filter-any"
                              onClick={() => setLanguageFilter('')}
                              className={`px-3 py-1 rounded-md text-[11.5px] font-semibold transition ${
                                languageFilter === ''
                                  ? 'bg-[#14110f] text-white'
                                  : 'bg-white border border-neutral-200 text-neutral-600 hover:border-[#f5b300]'
                              }`}>
                        Any
                      </button>
                      {availableLanguages.map((l) => (
                        <button key={l} data-testid={`book-lang-filter-${l.toLowerCase()}`}
                                onClick={() => setLanguageFilter(l)}
                                className={`px-3 py-1 rounded-md text-[11.5px] font-semibold transition ${
                                  languageFilter === l
                                    ? 'bg-[#14110f] text-white'
                                    : 'bg-white border border-neutral-200 text-neutral-600 hover:border-[#f5b300]'
                                }`}>
                          {l}
                        </button>
                      ))}
                    </div>
                  )}
                  <div data-testid="specialist-picker" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    <AnySpecialistCard
                      selected={advisorId === ''}
                      onSelect={() => { setAdvisorId(''); setSelectedSlot(null); }}
                    />
                    {filteredSpecialists.map((s) => (
                      <SpecialistCard
                        key={s.id}
                        specialist={s}
                        selected={advisorId === s.advisor_id}
                        onSelect={() => { setAdvisorId(s.advisor_id); setSelectedSlot(null); }}
                      />
                    ))}
                    {filteredSpecialists.length === 0 && (
                      <div className="col-span-full text-[12.5px] text-neutral-500 italic">
                        No specialists speak {languageFilter} for this service.{' '}
                        <button onClick={() => setLanguageFilter('')}
                                className="text-[#c89200] font-semibold hover:underline">
                          Clear filter
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-7">
            <div className="lg:col-span-7 rounded-xl border border-neutral-100 p-5 md:p-7 bg-white shadow-[0_20px_45px_-25px_rgba(20,17,15,0.18)]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[18px] font-bold text-[#14110f]">{monthLabel(new Date(view.year, view.month, 1))}</h3>
                <div className="flex items-center gap-2">
                  <button onClick={() => navMonth(-1)} className="w-9 h-9 rounded-full border border-neutral-200 hover:bg-[#f5b300] hover:border-[#f5b300] flex items-center justify-center transition"><ChevronLeft className="w-4 h-4" /></button>
                  <button onClick={() => navMonth(1)} className="w-9 h-9 rounded-full border border-neutral-200 hover:bg-[#f5b300] hover:border-[#f5b300] flex items-center justify-center transition"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
              {/* Calendar heading shows whose calendar we're viewing */}
              <p className="text-[12px] text-neutral-500 mb-4">
                Showing availability for {advisorId === ''
                  ? <strong className="text-[#14110f]">any available specialist</strong>
                  : <strong className="text-[#14110f]">{(specialists.find((s) => s.advisor_id === advisorId) || {}).name || 'selected specialist'}</strong>}
              </p>

              <div className="grid grid-cols-7 gap-1 text-center text-[11px] uppercase tracking-wider text-neutral-400 mb-1">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div key={d}>{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {matrix.map((d, i) => {
                  if (!d) return <div key={i} />;
                  const key = ymd(d);
                  const past = d < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                  const info = rangeMap[key];
                  const has = info?.has_availability;
                  const isSel = selectedDate && ymd(selectedDate) === key;
                  return (
                    <button
                      key={key}
                      disabled={past || !has}
                      onClick={() => setSelectedDate(d)}
                      className={`relative aspect-square rounded-lg flex flex-col items-center justify-center text-[14px] font-semibold transition-all ${
                        isSel ? 'bg-[#f5b300] text-[#14110f] shadow-[0_8px_22px_-10px_rgba(245,179,0,0.7)]'
                        : past ? 'text-neutral-300 cursor-not-allowed'
                        : has ? 'text-[#14110f] hover:bg-[#fff7e0] cursor-pointer'
                        : 'text-neutral-400 cursor-not-allowed line-through'
                      }`}
                    >
                      {d.getDate()}
                      {has && !isSel && <span className="absolute bottom-1.5 w-1 h-1 rounded-full bg-[#f5b300]" />}
                    </button>
                  );
                })}
              </div>
              {loadingRange && <div className="flex items-center gap-2 text-[12px] text-neutral-400 mt-3"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading availability…</div>}
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-xl border border-neutral-100 p-5 md:p-7 bg-white shadow-[0_20px_45px_-25px_rgba(20,17,15,0.18)] min-h-[420px]">
                <h3 className="text-[18px] font-bold text-[#14110f] mb-1">Available times</h3>
                <p className="text-[12.5px] text-neutral-500 mb-5">{selectedDate ? selectedDate.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' }) : 'Select a date to see times'}</p>
                {!selectedDate ? (
                  <div className="py-20 text-center text-neutral-400 text-[13px]">
                    <CalendarCheck className="w-10 h-10 mx-auto mb-3 text-neutral-300" />
                    Choose a date on the left
                  </div>
                ) : loadingSlots ? (
                  <div className="grid grid-cols-2 gap-2.5">
                    {[...Array(6)].map((_, i) => <div key={i} className="h-11 rounded-md bg-neutral-100 animate-pulse" />)}
                  </div>
                ) : slots.length === 0 ? (
                  <div className="py-12 text-center text-neutral-400 text-[13px]">No times available on this day.</div>
                ) : (
                  <div className="grid grid-cols-2 gap-2.5">
                    {slots.map((s) => {
                      const sel = selectedSlot && selectedSlot.start === s.start;
                      return (
                        <button
                          key={s.start + s.advisor_id}
                          onClick={() => setSelectedSlot(s)}
                          className={`px-3 py-2.5 rounded-md border text-[14px] font-semibold transition-all ${
                            sel ? 'bg-[#f5b300] border-[#f5b300] text-[#14110f] shadow-[0_8px_22px_-10px_rgba(245,179,0,0.7)]'
                            : 'border-neutral-200 hover:border-[#f5b300] hover:text-[#c89200]'
                          }`}
                        >
                          {fmtTime(s.start)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              {selectedSlot && (
                <button
                  onClick={proceed}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-7 py-4 rounded-md shadow-[0_14px_30px_-10px_rgba(245,179,0,0.7)] hover:-translate-y-0.5 transition-all"
                >
                  Continue with {fmtTime(selectedSlot.start)} <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
